import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import RoundBox from 'Components/_NewElements/RoundBox';
import * as React from 'react';
import {
  FilterField,
  HeaderTimeDisplay,
  isThread,
  ItemStatus,
  MessageLayout,
  timeFromDate,
} from '../../../components/Messaging/components';
import { Container, Item } from 'Components/Grid';
import TextArea from 'Components/InputHandler/components/TextArea';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { createVisitAndModifyAuth, jwtModify, patientSelect } from '../fetchers';
import { actions as sessionActions } from 'Store/session';
import { actions as messagingActions } from 'Store/messaging';
import { assertCapabilities, TDispatch, useAppDispatch, useAppSelector, ws_manager } from 'Store/index';
import ChatView from '../../../components/Messaging/components/ChatView';
import { IMessageListItem } from 'Store/messaging/interfaces';
import CircularProgressLocal from 'Components/CircularProgress';
import { messageFetchBuilder } from 'Store/messaging/fetchers/messages';
import { threadFetchBuilder } from 'Store/messaging/fetchers/threads';
import ConfirmationDialog from 'Components/ConfirmationDialog';
import Icon from 'Components/_NewElements/Icon';
import colors from '../../../config/theme/colors';
import ToolTip from 'Components/ToolTip';
import { AttachmentForm } from 'Components/_NewElements/GeneralForms/AttachmentsForm';
import { Checkbox, Radio } from '@mui/material';
import InputHandler from 'Components/InputHandler';
import { actions as settingsActions } from 'Store/settings';
import { StyledHeader } from '../components';
import { useIntl } from 'react-intl';
import { TTOPIC } from 'Store/messaging/types/TTopic';
import { GroupMessageItem } from './GroupMessages';
import { platforms } from '../../../config/platformSettings';
import { topicFetchBuilder } from 'Store/messaging/fetchers/topics';
import { NavigateFunction, useNavigate } from 'react-router';
import { localStorageSave } from 'Utility/localStorage';
import { clearAllPatientDataAndSettings } from 'Store/combinedActions';
import PlatformCapabilities from '../../../config/capabilities';
import { ICapabilityContextProps, withCapabilities } from 'Containers/CapabilityHandler';
import { IGroupACLMapping } from '../../../config/definitions/accessControl';
import RangeSlider from 'Components/InputHandler/components/RangeSlider';
import { equals } from 'ramda';
import { getPatientAgeInMonths } from 'Utility/patientInfo';
import { PatientCountryCode } from 'neuro-constants';
import { uniq } from 'Utility/ramdaReplacement';

const fetchNewJWT = async (
  res: Response,
  dispatch: TDispatch,
  platformToOpen: Platform,
  navigation: NavigateFunction,
  capabilityGroups: IGroupACLMapping,
): Promise<void> => {
  if (res.status === 200) {
    ws_manager.disconnect();

    const resText = await res.text();
    const jwtModified = await jwtModify(resText);
    if (!jwtModified) throw new Error('Attempting to open patient data failed due to jwtModify failed');
    dispatch(clearAllPatientDataAndSettings());
    dispatch(sessionActions.loadSession());
    const lifeCareSyncCapability = assertCapabilities([PlatformCapabilities.LIFECARE_DIAGNOSIS_SYNC], capabilityGroups);

    await sessionActions.preferredPlatforms(dispatch, lifeCareSyncCapability);
    await createVisitAndModifyAuth('messagingWithPatient', null, platformToOpen);
    dispatch(sessionActions.updateSession());

    localStorageSave('platform', platformToOpen);
    dispatch(sessionActions.setPlatform(platformToOpen));

    navigation('/');
  }
};

const filterThreads = (
  item: IThreadListItem | TTOPIC,
  settings: IFilterSettings,
  session: ISessionStore,
  userSelectedPlatforms?: string[],
  thrPatientNames?:
    | {
        lastNames: string;
        firstNames: string;
        ssn: string;
        ssnLocalization: PatientCountryCode;
        id: string;
      }[]
    | null,
): boolean => {
  const filterFields = ['platforms', ...Object.keys(settings || {})];

  return isThread(item)
    ? filterFields.every(field => {
        switch (field) {
          case 'platforms': {
            // If no user selected or available platforms, dont show thread
            if (!userSelectedPlatforms || userSelectedPlatforms.length === 0) return false;
            // Show thread if no tags defined
            if (!item.topicTags || item.topicTags?.length === 0) return true;
            // Show thread if topic tags include selected platforms
            return item.topicTags?.some(tag => userSelectedPlatforms.includes(tag));
          }

          case 'age': {
            const ageRange = settings['age'];
            const thisPatient = thrPatientNames?.find(thrPN => thrPN.id === item.patientId);
            if (!thisPatient?.ssn || !thisPatient?.ssnLocalization || !ageRange) return true;

            const patientAge =
              getPatientAgeInMonths(undefined, true, {
                ssn: thisPatient.ssn,
                ssnLocalization: thisPatient.ssnLocalization,
              }) / 12;

            // Patients age should be more or equal to age range minimum
            // Patients age should be less than (maximum + 1) eg. if the maximum is 18, every patient who is 18 but less than 19 should be counted
            return ageRange[0] <= patientAge && ageRange[1] + 1 > patientAge;
          }

          case 'status': {
            const includedStatuses = settings['status'] as Array<'ended'>;

            if (includedStatuses.includes('ended')) {
              return true;
            } else {
              return !item.completionTimestamp;
            }
          }
          case 'show': {
            const includedStatuses = settings['show'] as 'mineAndUnassigned' | 'unassigned' | 'all';
            if (includedStatuses === 'all') return true;
            if (includedStatuses === 'unassigned' && !item.assignedFor) return true;
            if (
              includedStatuses === 'mineAndUnassigned' &&
              (item?.assignedFor?.id === session.data?.useruuid || !item.assignedFor)
            )
              return true;

            return false;
          }
          default:
            break;
        }
        return true;
      })
    : filterFields.every(field => {
        switch (field) {
          case 'platforms': {
            if (!userSelectedPlatforms || userSelectedPlatforms.length === 0) return false;
            if (!item.tags || item.tags?.length === 0) return true;
            return item.tags?.some(tag => userSelectedPlatforms.includes(tag));
          }

          case 'status': {
            const includedStatuses = settings['status'] as Array<'ended'>;

            if (includedStatuses.includes('ended')) {
              return true;
            } else {
              return !item.closureTimestamp;
            }
          }
          case 'show': {
            const includedStatuses = settings['show'] as 'mineAndUnassigned' | 'unassigned' | 'all';
            if (includedStatuses === 'all') return true;
            if (includedStatuses === 'unassigned' && !item.assignedFor) return true;
            if (
              includedStatuses === 'mineAndUnassigned' &&
              (item?.assignedFor?.id === session.data?.useruuid || !item.assignedFor)
            )
              return true;

            return false;
          }
          default:
            break;
        }
        return true;
      });
};

const ThreadHeader = ({
  thr,
  fm,
}: {
  thr: IThreadListItem;
  fm: (id: string) => string;
  isPatient: boolean;
  showSenderIcon?: boolean;
}) => {
  const lastDate = thr.latestMessage?.timestamp || thr.creationTimestamp;

  const users: TCompactVaultUser[] = useAppSelector(s => s.session.orgUsers) || [];
  const thisHandler = (users || []).find(urs => urs.userId === thr.assignedFor?.id);

  const completed = !!thr.completionTimestamp;

  const patientNamesData = useAppSelector(s => s.messaging.patientNames);
  const thrPatientNames = patientNamesData?.['threadList'];
  const thisPatientSSN = thrPatientNames?.find(thrPN => thrPN.id === thr.patientId)?.ssn;

  return (
    <Container>
      {/* {showSenderIcon && (
        <div style={{ margin: '0 1rem' }}>
          <Item xs={'auto'}>
            <Icon icon={isPatient ? 'patient' : 'org'} />
          </Item>
        </div>
      )} */}
      <Item xs={true}>
        <div style={{ fontSize: '1.8rem' }}>{thr.topicTitle || fm('messaging.noTitle')}</div>
        <Container>
          <Item xs={12} md={5.6}>
            {lastDate ? (
              <HeaderTimeDisplay date={lastDate} color={completed ? 'gray' : undefined} />
            ) : (
              <div style={{ fontSize: '1.4rem' }}>{fm('messaging.noDate')}</div>
            )}
          </Item>
          <Item xs={true}>
            <ItemStatus item={thr} fm={fm} />
          </Item>
        </Container>
      </Item>
      <Item xs={5} md={4.5} style={{ paddingRight: '1rem' }}>
        <div style={{ fontSize: '1.6rem', fontWeight: 600, display: 'flex' }}>
          <Container justifyContent={'flex-end'}>
            <Item xs={'auto'} style={{ fontWeight: 400, fontSize: '1.4rem', marginRight: '1rem' }}>
              <div style={{ textAlign: 'end' }}>{fm('messaging.patient')}</div>
              <div style={{ textAlign: 'end' }}>{fm('messaging.handler')}</div>
            </Item>
            <Item xs={9}>
              <Container>
                <Item className="ellipsis" xs={true}>
                  {thr.patientLastNames + ' ' + thr.patientFirstNames}
                </Item>
                <Item>&nbsp;</Item>
                <Item>{`(${thisPatientSSN})`}</Item>
              </Container>
              <div className="ellipsis">
                {!thr.assignedFor
                  ? '-'
                  : thisHandler
                    ? `${thisHandler.lastNames} ${thisHandler.firstNames}`
                    : fm('general.unknown')}
              </div>
            </Item>
          </Container>
        </div>
      </Item>
    </Container>
  );
};

const ThreadOpen = ({ thread, fm, capabilityGroups = {} }: IThreadOpen) => {
  // const timedate = new Date(m.date);
  // const minutes = `${timedate.getMinutes().toString().length === 1 ? 0 : ''}${timedate.getMinutes()}`;

  const [discussionMessages, setDiscussionMessages] = React.useState<IMessageListItem[]>([]);

  const dispatch = useAppDispatch();
  const updateMessages = () => {
    void messageFetchBuilder<'list'>({
      name: 'list',
      queryParams: [thread.patientId, thread.id],
    }).then(res => {
      if (Array.isArray(res)) {
        setDiscussionMessages(res);
      }
    });
  };
  React.useEffect(() => {
    updateMessages();
  }, []);

  const thisUserId: string = useAppSelector(s => s.session.data?.useruuid) || '';
  const thisUserIsTheHandler = thread.assignedFor?.id === thisUserId;

  const [loading, setLoading] = React.useState<boolean>(false);

  const [newMessageBody, setNewMessageBody] = React.useState<string | null>(null);

  const sendNewMessage = () => {
    if (newMessageBody || acceptedAttachments.length > 0) {
      setLoading(true);
      void messagingActions
        .newMessageToThread(thread.patientId, thread, newMessageBody, acceptedAttachments)
        .then(() => {
          updateMessages();
          setNewMessageBody(null);
          setAcceptedAttachments([]);
          setLoading(false);
        });
    }
  };

  const assignUserToggle = async () => {
    if (thisUserIsTheHandler) {
      await threadFetchBuilder({ name: 'unassign', queryParams: [thread.id] });
    } else {
      await threadFetchBuilder({ name: 'assign', queryParams: [thread.id, thisUserId] });
    }
    void dispatch(messagingActions.loadThreadList());
  };

  const completeDiscussionToggle = async () => {
    if (thread.completionTimestamp) {
      await threadFetchBuilder({ name: 'uncomplete', queryParams: [thread.id] });
    } else {
      await threadFetchBuilder({ name: 'complete', queryParams: [thread.id] });
    }
    void dispatch(messagingActions.loadThreadList());
  };

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState<boolean>(false);
  const deleteDiscussion = async () => {
    setDeleteConfirmationOpen(true);
  };

  // Attachments that have been inputted through the attachments form, ready to be sent
  const [acceptedAttachments, setAcceptedAttachments] = React.useState<File[]>([]);

  // Temporary attachments used for handling the attachment form
  const [tempAttachments, setTempAttachments] = React.useState<File[] | undefined>(undefined);
  const handleTempFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAttachments([...(tempAttachments || []), e.target.files[0]]);
    }
  };

  const { messages } = useIntl();
  const navigation = useNavigate();

  const tagFormat = (value: string) => {
    if (platforms.includes(value as Platform)) return fm('header.platforms.' + value);
    if (Object.keys(messages).includes('messaging.tags.' + value)) {
      return messages['messaging.tags.' + value];
    } else return value;
  };

  const patientNamesData = useAppSelector(s => s.messaging.patientNames);
  const thrPatientNames = patientNamesData?.['threadList'];
  const thisPatientSSN = thrPatientNames?.find(thrPN => thrPN.id === thread.patientId)?.ssn;

  return (
    <>
      <MessageLayout
        infoFields={[
          <div key={'name'}>
            <span>{fm('messaging.name')}: </span>
            <span style={{ fontWeight: 600 }}>{thread.patientLastNames + ' ' + thread.patientFirstNames}</span>
          </div>,
          <div key={'ssn'}>
            <span>{fm('messaging.ssn')}: </span>
            <span style={{ fontWeight: 600 }}>{thisPatientSSN || '-'}</span>
          </div>,
          <div key={'tags'}>
            <span>{fm('messaging.tagsHeader')}: </span>
            {thread.topicTags.map((tag, i) => (
              <span key={tag} style={{ fontWeight: 600 }}>
                <>{tagFormat(tag)}</>
                {i < thread.topicTags.length - 1 && ', '}
              </span>
            ))}
          </div>,
        ]}
        buttons={[
          {
            text: (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {fm('messaging.openPatient')}
                <span style={{ padding: '0' }}>
                  <Icon icon={'rightMoveArrow'} />
                </span>
              </div>
            ),
            onClick: async () => {
              await patientSelect(thread.patientId).then(
                res =>
                  res &&
                  fetchNewJWT(
                    res,
                    dispatch,
                    (thread.topicTags.find(tag => platforms.includes(tag as Platform)) ?? 'ms') as Platform,
                    navigation,
                    capabilityGroups,
                  ),
              );
            },
            border: false,
          },
          {
            text: thisUserIsTheHandler ? 'messaging.unassignMeAsHandler' : 'messaging.assignMeAsHandler',
            onClick: assignUserToggle,
          },
          {
            text: thread.completionTimestamp ? 'messaging.markUncomplete' : 'messaging.markComplete',
            onClick: completeDiscussionToggle,
            colorScheme: 'warning',
          },
          {
            text: 'messaging.deleteDiscussion',
            onClick: () => deleteDiscussion(),
            colorScheme: 'error',
          },
        ]}
      >
        <>
          {discussionMessages.length > 0 && (
            <ChatView
              messages={discussionMessages}
              thread={thread}
              updateMessages={updateMessages}
              ended={thread.completionTimestamp}
            />
          )}
          {discussionMessages.length === 0 && <div></div>}
          {!thread.completionTimestamp && (
            <>
              {acceptedAttachments.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                  <div style={{ fontWeight: 600 }}>{fm('messaging.unsentAttachments')}</div>
                  {acceptedAttachments.map(file => (
                    <Container key={file.name}>
                      <Item>{file.name}</Item>
                      <Item sx={{ marginLeft: '1rem', cursor: 'pointer' }}>
                        <Icon
                          icon="close"
                          color="errorDefault"
                          button
                          onClick={() => {
                            const filteredAttachments = acceptedAttachments.filter(attch => attch.name !== file.name);
                            setAcceptedAttachments(filteredAttachments);
                          }}
                        />
                      </Item>
                    </Container>
                  ))}
                </div>
              )}
              <Container style={{ marginTop: '2rem' }}>
                <Item xs={true}>
                  <TextArea
                    editing
                    type="TextArea"
                    name="reply"
                    placeholder={fm('messaging.replyPlaceholder')}
                    rows={2}
                    fullWidth
                    id="messagingReplyBox"
                    autoHeight
                    value={newMessageBody || ''}
                    onChange={values => {
                      const newMessageString = values['reply'];
                      if (typeof newMessageString === 'string') {
                        setNewMessageBody(newMessageString);
                      }
                    }}
                  />
                </Item>
                <ToolTip
                  hover
                  title={fm('messaging.addAttachment')}
                  content={
                    <Item
                      xs={'auto'}
                      style={{ padding: '0 1rem', cursor: 'pointer' }}
                      onClick={() => setTempAttachments([])}
                    >
                      <Icon icon="attach" />
                    </Item>
                  }
                />
                <Item xs={'auto'}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <ActionButtonRounded
                      width="auto"
                      filled
                      height={3}
                      fontSize={15}
                      text={'messaging.send'}
                      disabled={!newMessageBody && acceptedAttachments.length === 0}
                      loading={loading}
                      onClick={() => sendNewMessage()}
                    />
                  </div>
                </Item>
              </Container>
            </>
          )}
        </>
      </MessageLayout>
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        text={fm('messaging.deleteDiscussionConfirmation')}
        cancel={{
          callback: e => {
            e.stopPropagation();
            setDeleteConfirmationOpen(false);
          },
        }}
        confirm={{
          callback: async e => {
            e.stopPropagation();
            //await threadFetchBuilder({ name: 'delete', queryParams: [thread.id] });
            await topicFetchBuilder({ name: 'delete', queryParams: [thread.topicId] });
            void dispatch(messagingActions.loadThreadList());
            setDeleteConfirmationOpen(false);
          },
        }}
      />
      <ConfirmationDialog
        open={!!tempAttachments}
        centeredText
        text={
          tempAttachments ? (
            <AttachmentForm fm={fm} attachmentData={tempAttachments} handleFileChange={handleTempFileChange} />
          ) : (
            <></>
          )
        }
        cancel={{
          callback: e => {
            e.stopPropagation();
            setTempAttachments(undefined);
          },
        }}
        confirm={{
          disabled: (tempAttachments || []).length === 0,
          text: 'general.accept',
          callback: async e => {
            e.stopPropagation();
            // Add temp attachments to accepted attachments data
            tempAttachments && setAcceptedAttachments([...acceptedAttachments, ...tempAttachments]);
            setTempAttachments(undefined);
          },
        }}
      />
    </>
  );
};

interface IThreadOpen extends ICapabilityContextProps {
  thread: IThreadListItem;
  fm: (id: string) => string;
}

const Messages = ({ capabilityGroups = {} }: ICapabilityContextProps) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const session = useAppSelector(s => s.session);

  const [openItemId, setOpenItemId] = React.useState<string | null>(null);

  const [loadingThreadsTime, setLoadingThreadsTime] = React.useState<boolean | number>(false);
  const dispatch = useAppDispatch();

  const reloadThreadsAndTopics = () => {
    setLoadingThreadsTime(false);

    const fetchPromises = [dispatch(messagingActions.loadTopics()), dispatch(messagingActions.loadThreadList())];

    void Promise.all(fetchPromises).then(([_topicStatus, threads]) => {
      if (Array.isArray(threads)) {
        const patientIDs = uniq(threads.map(thr => thr.patientId));
        dispatch(
          messagingActions.loadPatientNames(patientIDs, 'threadList', 'messaging main view for patient ssn display'),
        );
      }
      setTimeout(() => {
        setLoadingThreadsTime(Date.now());
      }, 500);
    });
  };

  React.useEffect(() => {
    reloadThreadsAndTopics();
    const reloadTimeout = setTimeout(() => {
      reloadThreadsAndTopics();
    }, 600000); // 10 mins
    return () => {
      clearTimeout(reloadTimeout);
    };
  }, []);

  const userSettingsFilterAgeRange = useAppSelector(s => s.settings.userSettings.messagingFilterAgeRange);
  const shownPlatforms = useAppSelector(
    s => s.settings.userSettings.userSelectedPlatforms || s.session.platforms?.available,
  );
  const availablePlatforms = useAppSelector((state: IState) => state.session.platforms?.available);

  const threads = useAppSelector(s => s.messaging.threads) || [];
  const topics = useAppSelector(s => s.messaging.topics) || [];
  const amountOfNoHandlers = threads.filter(t => !t.completionTimestamp && !t.assignedFor).length;

  const [filterSettings, setFilterSettings] = React.useState<IFilterSettings>({
    status: [],
    show: 'mineAndUnassigned',
    age: userSettingsFilterAgeRange ?? [0, 100],
  });

  const validTopics = topics.filter(to => to.targetPatients.length > 1);

  const patientNamesData = useAppSelector(s => s.messaging.patientNames);
  const thrPatientNames = patientNamesData?.['threadList'];

  const activeListItems: Array<IThreadListItem | TTOPIC> = [...threads, ...validTopics]
    .filter(item => filterThreads(item, filterSettings, session, shownPlatforms, thrPatientNames))
    .filter(item => !item.deletionTimestamp)
    .filter(item =>
      isThread(item) ? !item.completionTimestamp : !item.closureTimestamp || Date.now() < item.closureTimestamp,
    )
    .toSorted((a, b) => {
      const aDate = isThread(a) ? a.latestMessage?.timestamp || a.creationTimestamp : a.creationTimestamp;
      const bDate = isThread(b) ? b.latestMessage?.timestamp || b.creationTimestamp : b.creationTimestamp;
      return bDate - aDate;
    });

  const endedListItems: Array<IThreadListItem | TTOPIC> = [...threads, ...validTopics]
    .filter(item => filterThreads(item, filterSettings, session, shownPlatforms))
    .filter(item => !item.deletionTimestamp)
    .filter(item =>
      isThread(item) ? item.completionTimestamp : item.closureTimestamp && Date.now() >= item.closureTimestamp,
    )
    .toSorted((a, b) => {
      const aDate = isThread(a) ? a.latestMessage?.timestamp || a.creationTimestamp : a.creationTimestamp;
      const bDate = isThread(b) ? b.latestMessage?.timestamp || b.creationTimestamp : b.creationTimestamp;
      return bDate - aDate;
    });

  const activeThreadsAssignedToCurrentUser = [...threads, ...validTopics].filter(
    t => (isThread(t) ? !t.completionTimestamp : !t.closureTimestamp) && t.assignedFor?.id === session.data?.useruuid,
  );
  const amountOfThreadsAssignedToCurrentUser = activeThreadsAssignedToCurrentUser.length;
  const amountOfUnansweredThreadsAssignedToCurrentUser = activeThreadsAssignedToCurrentUser.filter(
    t => isThread(t) && t.latestMessage?.application === 'myneuro',
  ).length;

  // For selecting new userSelectedPlatforms in the dialog
  const [newSelectedPlatforms, setNewSelectedPlatforms] = React.useState<Platform[] | null>(null);
  const openPlatformSelectDialog = () => {
    setNewSelectedPlatforms(shownPlatforms || []);
  };

  // Keep track of timeout for updating user settings
  const [updateUsersettingsTimeout, setUpdateUsersettingsTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const changeFilterSettings = (values: IFilterSettings) => {
    // If the age range is updated, then save it to user settings after the timeout
    if (!equals(filterSettings.age, values.age)) {
      // Remove previous timer
      if (updateUsersettingsTimeout) {
        clearTimeout(updateUsersettingsTimeout);
        setUpdateUsersettingsTimeout(null);
      }
      setUpdateUsersettingsTimeout(
        setTimeout(() => {
          void settingsActions.createUserSettingVault({ messagingFilterAgeRange: values.age })(dispatch);
        }, 3000), // 3s
      );
    }

    setFilterSettings(values);
    setOpenItemId(null);
  };

  const lastSenderIsPatient = (thread: IThreadListItem) => {
    if (thread.latestMessage && thread.latestMessage.application === 'myneuro') {
      return true;
    }
    return false;
  };

  return (
    <div>
      <Container style={{ marginBottom: '2rem' }}>
        <Item xs={true}>
          <StyledHeader>{fm('messaging.title')}</StyledHeader>
          {/* <div>{fm('messaging.messagingInfoText')}</div> */}
        </Item>
        <Item xs={5} md={4}>
          <Container justifyContent={'flex-end'} alignItems="center">
            {typeof loadingThreadsTime === 'number' && (
              <Item style={{ fontSize: '1.4rem', marginRight: '2rem' }}>
                {fm('messaging.refreshed')}: {timeFromDate(new Date(loadingThreadsTime))}
              </Item>
            )}
            <Item>
              <ActionButtonRounded
                text={<Icon icon="refresh" />}
                width={2.5}
                height={4}
                fontSize={18}
                onClick={() => reloadThreadsAndTopics()}
                toolTip={<>{fm('messaging.refreshMessages')}</>}
              />
            </Item>
          </Container>
        </Item>
      </Container>
      <RoundBox style={{ padding: '1rem' }}>
        <Container>
          <Item xs={6} md={5} style={{ alignSelf: 'center' }}>
            <FilterField title={fm('messaging.show')} type="col">
              <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                {['mineAndUnassigned', 'all'].map(showField => {
                  const showValues = filterSettings['show'] || '';
                  return (
                    <div
                      key={showField}
                      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => {
                        if (filterSettings['show'] !== showField) {
                          changeFilterSettings({ ...filterSettings, show: showField });
                        }
                      }}
                    >
                      <Radio
                        value={showField}
                        style={{ padding: 0, margin: 0 }}
                        checked={showValues.includes(showField)}
                      />
                      <span style={{ margin: '0 1rem' }}>
                        {fm('messaging.statusOpt.' + showField)}
                        {showField === 'mineAndUnassigned' &&
                        amountOfThreadsAssignedToCurrentUser + amountOfNoHandlers > 0 ? (
                          <span
                            style={{
                              fontWeight: amountOfUnansweredThreadsAssignedToCurrentUser > 0 ? 600 : 400,
                            }}
                          >{` (${amountOfThreadsAssignedToCurrentUser + amountOfNoHandlers})`}</span>
                        ) : (
                          ''
                        )}
                        {showField === 'unassigned' && amountOfNoHandlers > 0 ? (
                          <span>{` (${amountOfNoHandlers})`}</span>
                        ) : (
                          ''
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex' }}>
                {['ended'].map(statusField => {
                  const statusValues = filterSettings['status'] || [];
                  return (
                    <div key={statusField} style={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        value={statusField}
                        style={{ padding: 0, margin: 0 }}
                        checked={statusValues.includes(statusField)}
                        onChange={e => {
                          if (e.currentTarget.checked) {
                            changeFilterSettings({
                              ...filterSettings,
                              status: [...statusValues, statusField],
                            });
                          } else {
                            changeFilterSettings({
                              ...filterSettings,
                              status: statusValues.filter((s: string) => s !== statusField),
                            });
                          }
                        }}
                      />
                      <span style={{ margin: '0 1rem' }}>{fm('messaging.statusOpt.' + statusField)}</span>
                    </div>
                  );
                })}
              </div>
            </FilterField>
          </Item>
          <Item xs={true} style={{ alignSelf: 'center' }}>
            <FilterField title={fm('messaging.platformsToShow')} type="col">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ WebkitLineClamp: 4 }} className="ellipsis-multiline">
                  {(shownPlatforms || []).map((p, i, l) => (
                    <React.Fragment key={p}>{`${fm('header.platforms.' + p)}${
                      i !== l.length - 1 ? '; ' : ''
                    }`}</React.Fragment>
                  ))}
                </div>
                <div
                  className="link"
                  style={{ textDecoration: 'underline', margin: '0 1rem' }}
                  onClick={() => openPlatformSelectDialog()}
                >
                  {fm('general.edit')}
                </div>
              </div>
            </FilterField>
            <FilterField title={fm('messaging.patientAge')} type="row">
              <div style={{ width: '30rem', marginTop: '0.7rem' }}>
                <RangeSlider
                  type="RangeSlider"
                  name="age"
                  editing
                  min={0}
                  max={100}
                  value={filterSettings['age']}
                  compact
                  height={5}
                  onChange={val => {
                    const ageRange = val['age'];
                    if (
                      ageRange &&
                      Array.isArray(ageRange) &&
                      ageRange.length === 2 &&
                      ageRange.every(i => typeof i === 'number')
                    ) {
                      changeFilterSettings({
                        ...filterSettings,
                        age: ageRange as [number, number],
                      });
                    }
                  }}
                />
              </div>
            </FilterField>
          </Item>
        </Container>
      </RoundBox>
      <div style={{ marginTop: '2rem' }}>
        {!loadingThreadsTime ? (
          <Container justifyContent={'center'}>
            <Item>
              <CircularProgressLocal />
            </Item>
          </Container>
        ) : (
          <>
            {activeListItems.map(item => (
              <Container key={item.id} style={{ marginBottom: '1rem' }}>
                <Item xs={'auto'} style={{ margin: '1.8rem 1rem 0 1rem' }}>
                  <Icon
                    icon={isThread(item) && lastSenderIsPatient(item) ? 'patient' : 'org'}
                    color={
                      isThread(item) && lastSenderIsPatient(item) ? 'mysq' : isThread(item) ? undefined : 'warningDark'
                    }
                  />
                </Item>
                <Item xs={true}>
                  {isThread(item) ? (
                    <BlockWrapper
                      collapse={{
                        open: openItemId === item.id,
                        collapseHandler: () => {
                          setOpenItemId(openItemId === item.id ? null : item.id);
                        },
                      }}
                      color={lastSenderIsPatient(item) ? 'mysq' : item.completionTimestamp ? 'gray' : 'light'}
                      title={<ThreadHeader thr={item} fm={fm} isPatient={lastSenderIsPatient(item)} showSenderIcon />}
                      contentMargin={2}
                      compact
                    >
                      {openItemId === item.id && <ThreadOpen thread={item} fm={fm} />}
                    </BlockWrapper>
                  ) : (
                    <GroupMessageItem
                      open={openItemId === item.id}
                      setOpen={() => setOpenItemId(openItemId === item.id ? null : item.id)}
                      topic={item}
                      reloadTopics={() => reloadThreadsAndTopics()}
                    />
                  )}
                </Item>
              </Container>
            ))}
            {endedListItems.length > 0 && (
              <div>
                <h3 style={{ color: colors.primary, margin: '2rem 0 1rem 0' }}>
                  {fm('messaging.completedDiscussions')}
                </h3>
                {endedListItems.map(item => (
                  <Container key={item.id} style={{ marginBottom: '1rem' }}>
                    <Item xs={'auto'} style={{ margin: '1.8rem 1rem 0 1rem' }}>
                      <Icon icon={isThread(item) && lastSenderIsPatient(item) ? 'patient' : 'org'} color={'grey'} />
                    </Item>
                    <Item xs={true}>
                      {isThread(item) ? (
                        <BlockWrapper
                          collapse={{
                            open: openItemId === item.id,
                            collapseHandler: () => {
                              setOpenItemId(openItemId === item.id ? null : item.id);
                            },
                          }}
                          color={item.completionTimestamp ? 'gray' : 'light'}
                          title={<ThreadHeader thr={item} fm={fm} isPatient={lastSenderIsPatient(item)} />}
                          contentMargin={2}
                          compact
                        >
                          {openItemId === item.id && <ThreadOpen thread={item} fm={fm} />}
                        </BlockWrapper>
                      ) : (
                        <GroupMessageItem
                          open={openItemId === item.id}
                          setOpen={() => setOpenItemId(openItemId === item.id ? null : item.id)}
                          topic={item}
                          reloadTopics={() => reloadThreadsAndTopics()}
                          color={'gray'}
                        />
                      )}
                    </Item>
                  </Container>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmationDialog
        open={!!newSelectedPlatforms}
        transitionDuration={{ exit: 0 }}
        text={
          <>
            <div style={{ marginBottom: '1rem' }}>{fm('messaging.selectPlatforms')}</div>
            <div>
              <InputHandler
                type="Checkbox"
                editing={true}
                name="userSelectedPlatforms"
                formData={{
                  document: {
                    userSelectedPlatforms: newSelectedPlatforms || shownPlatforms,
                  },
                  onChange: values => setNewSelectedPlatforms(values['userSelectedPlatforms'] as Platform[]),
                }}
                options={availablePlatforms}
                optionFormatter={(name: string | number): React.JSX.Element | string => fm('header.platforms.' + name)}
              />
            </div>
          </>
        }
        cancel={{ callback: () => setNewSelectedPlatforms(null) }}
        confirm={{
          callback: () => {
            newSelectedPlatforms &&
              void settingsActions.createUserSettingVault({
                userSelectedPlatforms: newSelectedPlatforms,
              })(dispatch);

            setNewSelectedPlatforms(null);
          },
        }}
      />
    </div>
  );
};

interface IFilterSettings {
  platforms?: Platform[];
  age?: [number, number];
  status?: string[];
  show?: string;
}

export default withCapabilities(Messages);
